import axiosWithAuth from "../utils/axiosWithAuth";
import { backUrl } from "../utils/backURL";
const API_URL = backUrl;

// ===== FUNCIONES PARA MANEJO DE CLIENTES =====

/**
 * Obtener todos los clientes con filtros opcionales
 * @param {string} clientId - ID del cliente principal
 * @param {object} filters - Filtros opcionales (name, email, status, is_company, country)
 * @returns {Promise} - Promesa con la respuesta de la API
 */
export const getClients = async (clientId, filters = {}) => {
  try {
    // Construir query parameters para los filtros
    const queryParams = new URLSearchParams();
    
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
        queryParams.append(key, filters[key]);
      }
    });

    const queryString = queryParams.toString();
    const url = `${API_URL}/workflows/p2p/${clientId}/clients${queryString ? `?${queryString}` : ''}`;
    
    console.log(`Obteniendo clientes para clientId: ${clientId}`, filters);
    
    const response = await axiosWithAuth.get(url);
    return response.data;
  } catch (error) {
    console.error("Error obteniendo clientes:", error);
    throw error;
  }
};

/**
 * Obtener un cliente específico por ID
 * @param {string} clientId - ID del cliente principal
 * @param {string} customClientId - ID del cliente personalizado
 * @returns {Promise} - Promesa con la respuesta de la API
 */
export const getClientById = async (clientId, customClientId) => {
  try {
    if (!clientId || !customClientId) {
      throw new Error("clientId y customClientId son requeridos");
    }

    const url = `${API_URL}/workflows/p2p/${clientId}/clients/${customClientId}`;
    
    console.log(`Obteniendo cliente ${customClientId} para clientId: ${clientId}`);
    
    const response = await axiosWithAuth.get(url);
    return response.data;
  } catch (error) {
    console.error("Error obteniendo cliente por ID:", error);
    throw error;
  }
};

/**
 * Crear un nuevo cliente
 * @param {string} clientId - ID del cliente principal
 * @param {object} clientData - Datos del cliente a crear
 * @returns {Promise} - Promesa con la respuesta de la API
 */
export const createClient = async (clientId, clientData) => {
  try {
    if (!clientId) {
      throw new Error("clientId es requerido");
    }

    if (!clientData || !clientData.name) {
      throw new Error("Los datos del cliente y el nombre son requeridos");
    }

    const url = `${API_URL}/workflows/p2p/${clientId}/clients`;
    
    console.log(`Creando cliente para clientId: ${clientId}`, clientData);
    
    const response = await axiosWithAuth.post(url, clientData);
    return response.data;
  } catch (error) {
    console.error("Error creando cliente:", error);
    throw error;
  }
};

/**
 * Actualizar un cliente existente
 * @param {string} clientId - ID del cliente principal
 * @param {string} customClientId - ID del cliente personalizado
 * @param {object} clientData - Datos del cliente a actualizar
 * @returns {Promise} - Promesa con la respuesta de la API
 */
export const updateClient = async (clientId, customClientId, clientData) => {
  try {
    if (!clientId || !customClientId) {
      throw new Error("clientId y customClientId son requeridos");
    }

    if (!clientData || Object.keys(clientData).length === 0) {
      throw new Error("Se requieren datos para actualizar el cliente");
    }

    const url = `${API_URL}/workflows/p2p/${clientId}/clients/${customClientId}`;
    
    console.log(`Actualizando cliente ${customClientId} para clientId: ${clientId}`, clientData);
    
    const response = await axiosWithAuth.put(url, clientData);
    return response.data;
  } catch (error) {
    console.error("Error actualizando cliente:", error);
    throw error;
  }
};

/**
 * Eliminar un cliente
 * @param {string} clientId - ID del cliente principal
 * @param {string} customClientId - ID del cliente personalizado
 * @param {object} additionalData - Datos adicionales como user_name para logs
 * @returns {Promise} - Promesa con la respuesta de la API
 */
export const deleteClient = async (clientId, customClientId, additionalData = {}) => {
  try {
    if (!clientId || !customClientId) {
      throw new Error("clientId y customClientId son requeridos");
    }

    const url = `${API_URL}/workflows/p2p/${clientId}/clients/${customClientId}`;
    
    console.log(`Eliminando cliente ${customClientId} para clientId: ${clientId}`);
    
    const response = await axiosWithAuth.delete(url, { data: additionalData });
    return response.data;
  } catch (error) {
    console.error("Error eliminando cliente:", error);
    throw error;
  }
};

// ===== FUNCIONES AUXILIARES =====

/**
 * Obtener estadísticas de clientes
 * @param {string} clientId - ID del cliente principal
 * @returns {Promise} - Promesa con estadísticas de clientes
 */
export const getClientsStats = async (clientId) => {
  try {
    const response = await getClients(clientId);
    
    if (response.success && response.data) {
      const clients = response.data;
      const stats = {
        total: clients.length,
        companies: clients.filter(client => client.is_company).length,
        individuals: clients.filter(client => !client.is_company).length,
        active: clients.filter(client => client.status === 'active').length,
        inactive: clients.filter(client => client.status === 'inactive').length,
        countries: [...new Set(clients.map(client => client.country).filter(Boolean))].length
      };
      
      return {
        success: true,
        data: stats
      };
    }
    
    return response;
  } catch (error) {
    console.error("Error obteniendo estadísticas de clientes:", error);
    throw error;
  }
};

/**
 * Buscar clientes por nombre o email
 * @param {string} clientId - ID del cliente principal
 * @param {string} searchTerm - Término de búsqueda
 * @returns {Promise} - Promesa con clientes filtrados
 */
export const searchClients = async (clientId, searchTerm) => {
  try {
    if (!searchTerm || searchTerm.trim().length < 2) {
      throw new Error("El término de búsqueda debe tener al menos 2 caracteres");
    }

    // Buscar tanto por nombre como por email
    const [nameResults, emailResults] = await Promise.all([
      getClients(clientId, { name: searchTerm.trim() }),
      getClients(clientId, { email: searchTerm.trim() })
    ]);

    // Combinar resultados y eliminar duplicados
    const allResults = [];
    const seenIds = new Set();

    const addResults = (results) => {
      if (results.success && results.data) {
        results.data.forEach(client => {
          if (!seenIds.has(client.id)) {
            seenIds.add(client.id);
            allResults.push(client);
          }
        });
      }
    };

    addResults(nameResults);
    addResults(emailResults);

    return {
      success: true,
      data: allResults,
      count: allResults.length
    };
  } catch (error) {
    console.error("Error buscando clientes:", error);
    throw error;
  }
};

/**
 * Validar datos de cliente antes de crear/actualizar
 * @param {object} clientData - Datos del cliente a validar
 * @returns {object} - Resultado de la validación
 */
export const validateClientData = (clientData) => {
  const errors = [];
  const warnings = [];

  // Validaciones requeridas
  if (!clientData.name || clientData.name.trim().length === 0) {
    errors.push("El nombre del cliente es requerido");
  }

  // Validaciones de formato
  if (clientData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clientData.email)) {
    errors.push("El formato del email no es válido");
  }

  if (clientData.phone && clientData.phone.length < 10) {
    warnings.push("El número de teléfono parece ser demasiado corto");
  }

  // Validaciones de negocio
  if (clientData.name && clientData.name.length > 100) {
    errors.push("El nombre no puede exceder los 100 caracteres");
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};
